import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, Popover, Box, IconButton, Typography, Button, Chip, Alert } from '@mui/material';
import { Close, ChevronLeft, ChevronRight, CalendarMonth, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight, Clear, AccessTimeOutlined } from '@mui/icons-material';
import { TimePickerInline, format12h } from './TimePickerModal';
import { useTranslation } from 'react-i18next';
import {
  DATE_PICKER_MONTHS,
  DATE_PICKER_WEEKDAYS,
  DATE_PICKER_RANGE_LABELS,
  DATE_PICKER_QUICK_OPTIONS,
  DATE_PICKER_BACK_TO_LIST,
  DATE_PICKER_QUARTERS
} from './appConstants';
import './DatePickerModal.scss';

const MONTHS = DATE_PICKER_MONTHS;

const MODE_DATE_RANGE = 'dateRange';
const MODE_MONTH = 'month';
const MODE_YEAR = 'year';
const MODE_QUARTER = 'quarter';

const DatePickerModal = ({ 
  open, 
  onClose, 
  onDateChange,
  anchorEl,
  mode = MODE_DATE_RANGE, // 'dateRange', 'month', 'year', 'quarter'
  currentDate,
  selectedDates,
  title,
  minDays = 2,
  maxDays = 10,
  helperText,
  allowSingleSelection = false,
  autoApplySingleSelection = false,
  selectedStartDate,
  selectedDueDate,
  onSelectionChange,
  onApply,
  onClearStartDate,
  onClearDueDate,
  showTime = false,
  selectedStartTime = '',
  selectedDueTime = '',
  hideStartDate = false,
  hideDueDate = false,
  hideCards = false,
  hideActions = false,
  maxDate = null,
  minDate = null,
  disabledDates = []
}) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [leftView, setLeftView] = useState('date'); // 'date', 'time'
  const [rightView, setRightView] = useState('date'); // 'date', 'time'
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(0);
  const [selectedQuick, setSelectedQuick] = useState('');
  const [fromCalendarMonth, setFromCalendarMonth] = useState(() => new Date());
  const [toCalendarMonth, setToCalendarMonth] = useState(() => new Date());
  const yearScrollRef = useRef(null);

  // Scroll to the current year as soon as the year list mounts
  const yearScrollCallbackRef = useCallback((node) => {
    if (node && mode === MODE_YEAR && open) {
      yearScrollRef.current = node;
      
      // Set scroll position immediately
      const targetYear = currentDate ? currentDate.getFullYear() : new Date().getFullYear();
      const yearElement = node.querySelector(`[data-year="${targetYear}"]`);
      
      if (yearElement) {
        yearElement.scrollIntoView({ 
          behavior: 'auto', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }
  }, [mode, open, currentDate]);

  // Sync local state when modal opens or selection changes
  useEffect(() => {
    if (open) {
      setLeftView('date');
      setRightView('date');
      const hasExplicitRangeSelection = selectedStartDate !== undefined || selectedDueDate !== undefined;

      if (mode === MODE_DATE_RANGE && hasExplicitRangeSelection) {
        setFromDate(selectedStartDate || '');
        setToDate(selectedDueDate || '');
        if (showTime) {
          setStartTime(selectedStartTime || '');
          setDueTime(selectedDueTime || '');
        }
        setError('');
        setSelectedQuick(allowSingleSelection && autoApplySingleSelection ? 'custom' : '');
      } else if (mode === MODE_DATE_RANGE && selectedDates && selectedDates.length > 0) {
        setFromDate(selectedDates[0]);
        setToDate(selectedDates[selectedDates.length - 1]);
        setError(''); // Clear errors only for date range mode
        setSelectedQuick(allowSingleSelection && autoApplySingleSelection ? 'custom' : '');
      } else if (mode === MODE_DATE_RANGE) {
        setFromDate('');
        setToDate('');
        if (showTime) {
          setStartTime('');
          setDueTime('');
        }
        setError('');
        setSelectedQuick('');
      }
      if (mode === MODE_DATE_RANGE && allowSingleSelection && autoApplySingleSelection) {
        setSelectedQuick('custom');
      }
      if (currentDate) {
        setSelectedMonth(currentDate.getMonth());
        setSelectedYear(currentDate.getFullYear());
        setSelectedQuarter(Math.floor(currentDate.getMonth() / 3));
      }
      if (mode === MODE_DATE_RANGE) {
        let base = currentDate ? new Date(currentDate) : new Date();
        
        if (disabledDates && disabledDates.length > 0) {
           const tempToYmd = (dateObj) => {
             const y = dateObj.getFullYear();
             const m = String(dateObj.getMonth() + 1).padStart(2, '0');
             const d = String(dateObj.getDate()).padStart(2, '0');
             return `${y}-${m}-${d}`;
           };
           let ymd = tempToYmd(base);
           let attempts = 0;
           while (disabledDates.includes(ymd) && attempts < 1000) {
              base.setDate(base.getDate() + 1);
              ymd = tempToYmd(base);
              attempts++;
           }
        }

        const fromBase = selectedDates?.[0] ? new Date(selectedDates[0]) : base;
        const toBase = selectedDates?.[selectedDates.length - 1]
          ? new Date(selectedDates[selectedDates.length - 1])
          : base;
        setFromCalendarMonth(new Date(fromBase.getFullYear(), fromBase.getMonth(), 1));
        setToCalendarMonth(new Date(toBase.getFullYear(), toBase.getMonth(), 1));
      }
    }
  }, [open, selectedDates, selectedStartDate, selectedDueDate, selectedStartTime, selectedDueTime, showTime, currentDate, mode, allowSingleSelection, autoApplySingleSelection]);


  // Validate and apply a date-range selection
  const handleDateRangeApply = () => {
    if (!allowSingleSelection && (!fromDate || !toDate)) {
      setError(t('calendar.selectBothDates'));
      return;
    }

    const dates = [];
    
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      if (from > to) {
        setError(t('calendar.fromDateBeforeToDate'));
        return;
      } else if (from.getTime() === to.getTime() && showTime && startTime && dueTime) {
        const [startH, startM] = startTime.split(':').map(Number);
        const [dueH, dueM] = dueTime.split(':').map(Number);
        if (dueH < startH || (dueH === startH && dueM < startM)) {
          setError(t('taskModal.dueTimeBeforeStartTime'));
          return;
        }
      }

      if (!allowSingleSelection) {
        const daysDiff = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
        
        if (daysDiff < minDays) {
          setError(t('calendar.selectAtLeast4Days').replace('4', minDays));
          return;
        }
        
        if (daysDiff > maxDays) {
          setError(t('calendar.selectMaximum10Days').replace('10', maxDays));
          return;
        }
      }
      
      const current = new Date(from);
      while (current <= to) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
    } else {
      if (fromDate) dates.push(fromDate);
      if (toDate) dates.push(toDate);
    }
    
    onDateChange?.(dates);
    if (showTime) {
      onApply?.({ fromDate, toDate, dates, startTime, dueTime });
    } else {
      onApply?.({ fromDate, toDate, dates });
    }
    setError('');
    onClose();
  };

  const toYmd = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getNextWeekday = (date, targetDay, includeToday = false) => {
    const day = date.getDay();
    let delta = (targetDay - day + 7) % 7;
    if (!includeToday && delta === 0) delta = 7;
    const result = new Date(date);
    result.setDate(date.getDate() + delta);
    return result;
  };

  const getQuickOptions = () => {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const nextWeek = getNextWeekday(todayStart, 1, false);
    const twoWeeks = new Date(todayStart);
    twoWeeks.setDate(todayStart.getDate() + 14);
    const fourWeeks = new Date(todayStart);
    fourWeeks.setDate(todayStart.getDate() + 28);

    return [
      {
        id: 'today',
        label: DATE_PICKER_QUICK_OPTIONS.TODAY,
        right: todayStart.toLocaleDateString('en-US', { weekday: 'short' }),
        date: todayStart
      },
      {
        id: 'tomorrow',
        label: DATE_PICKER_QUICK_OPTIONS.TOMORROW,
        right: new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate() + 1)
          .toLocaleDateString('en-US', { weekday: 'short' }),
        date: new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate() + 1)
      },
      {
        id: 'next-week',
        label: DATE_PICKER_QUICK_OPTIONS.NEXT_WEEK,
        right: nextWeek.toLocaleDateString('en-US', { day: 'numeric', month: 'long' }),
        date: nextWeek
      },
      {
        id: 'two-weeks',
        label: DATE_PICKER_QUICK_OPTIONS.TWO_WEEKS,
        right: twoWeeks.toLocaleDateString('en-US', { day: 'numeric', month: 'long' }),
        date: twoWeeks
      },
      {
        id: 'four-weeks',
        label: DATE_PICKER_QUICK_OPTIONS.FOUR_WEEKS,
        right: fourWeeks.toLocaleDateString('en-US', { day: 'numeric', month: 'long' }),
        date: fourWeeks
      },
      {
        id: 'custom',
        label: DATE_PICKER_QUICK_OPTIONS.CUSTOM
      }
    ];
  };

  const handleQuickSelect = (option) => {
    setSelectedQuick(option.id);
    setError('');
    if (option.id === 'custom') return;
    if (option.date) {
      const next = toYmd(option.date);
      setFromDate(next);
      setFromCalendarMonth(new Date(option.date.getFullYear(), option.date.getMonth(), 1));
      onSelectionChange?.({ fromDate: next, toDate, dates: [next] });
    }
  };

  const handleClearStartDate = (event) => {
    event.stopPropagation();
    setFromDate('');
    setStartTime('');
    setError('');
    setSelectedQuick('custom');
    onSelectionChange?.({ fromDate: '', toDate, dates: toDate ? [toDate] : [] });
    onClearStartDate?.();
  };

  const handleClearDueDate = (event) => {
    event.stopPropagation();
    setToDate('');
    setDueTime('');
    setError('');
    setSelectedQuick('custom');
    onSelectionChange?.({ fromDate, toDate: '', dates: fromDate ? [fromDate] : [] });
    onClearDueDate?.();
  };

  const getCalendarDays = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const prevLastDay = new Date(year, month, 0);
    const firstDayOfWeek = firstDay.getDay();
    const days = [];

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevLastDay.getDate() - i;
      const dateObj = new Date(year, month - 1, day);
      days.push({
        date: day,
        isCurrentMonth: false,
        dateString: toYmd(dateObj),
        dateObj
      });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dateObj = new Date(year, month, i);
      days.push({
        date: i,
        isCurrentMonth: true,
        dateString: toYmd(dateObj),
        dateObj
      });
    }

    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        const dateObj = new Date(year, month + 1, i);
        days.push({
          date: i,
          isCurrentMonth: false,
          dateString: toYmd(dateObj),
          dateObj
        });
      }
    }

    return days;
  };

  // Apply a selected month
  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(selectedYear, monthIndex, 1);
    onDateChange?.(newDate);
    onClose();
  };

  // Navigate year when selecting a month
  const handleYearChange = (direction) => {
    setSelectedYear(prev => prev + direction);
  };


  // Apply a selected quarter
  const handleQuarterSelect = (quarterIndex) => {
    setSelectedQuarter(quarterIndex);
    const newDate = new Date(selectedYear, quarterIndex * 3, 1);
    onDateChange?.(newDate);
    onClose();
  };

  // Apply a selected year
  const handleYearSelect = (year) => {
    setSelectedYear(year);
    const newDate = new Date(year, 0, 1);
    onDateChange?.(newDate);
    onClose();
  };

  // Build the list of years around the current year
  const generateYears = () => {
    const baseYear = currentDate ? currentDate.getFullYear() : new Date().getFullYear();
    const years = [];
    for (let i = baseYear - 50; i <= baseYear + 50; i++) {
      years.push(i);
    }
    return years;
  };



  const defaultTitle = hideStartDate || hideDueDate
    ? (showTime ? t('calendar.selectDateAndTime') : t('calendar.selectDate'))
    : (showTime ? t('calendar.selectDatesAndTime') : t('calendar.selectDates'));

  const content = (
    <Box className="date-picker-modal">
      <Box className="date-picker-header">
        <Typography variant="h6" className="date-picker-title">
          {title || defaultTitle}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>
      <Box className="date-picker-header-divider" />
      
      {mode === MODE_DATE_RANGE && error && (
        <Alert severity="error" className="date-picker-alert">
          {error}
        </Alert>
      )}
      
      {mode === MODE_DATE_RANGE && (
        <>
          {!hideCards && (
            <Box className={`date-picker-cards ${hideStartDate || hideDueDate ? 'date-picker-cards--single' : 'date-picker-cards--double'}`}>
              {/* Start Card */}
              {!hideStartDate && (
              <Box className="date-picker-card">
              <Box
                className={`date-picker-card-date is-clickable${leftView === 'date' && selectedQuick === 'custom' ? ' is-active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setLeftView('date');
                  setSelectedQuick('custom');
                }}
              >
                <Box className="date-picker-card-main">
                  <CalendarMonth className="date-picker-card-icon" />
                  <Typography className="date-picker-card-label">{DATE_PICKER_RANGE_LABELS.FROM}</Typography>
                  <Typography className={`date-picker-card-value${!fromDate ? ' date-picker-card-value--empty' : ''}`}>
                    {fromDate ? new Date(fromDate).toLocaleDateString('en-US') : DATE_PICKER_RANGE_LABELS.FROM_PLACEHOLDER || t('calendar.selectDate', 'Select date')}
                  </Typography>
                </Box>
                {onClearStartDate && fromDate && (
                  <IconButton
                    size="small"
                    color="primary"
                    className="date-picker-clear-button"
                    onClick={(e) => { e.stopPropagation(); handleClearStartDate(e); }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
              {showTime && (
                <>
                  <Box className="date-picker-card-divider" />
                  <Box 
                    className={`date-picker-card-time${fromDate ? ' is-clickable' : ' date-picker-card-time--disabled'}${leftView === 'time' && fromDate ? ' is-active' : ''}`}
                    onClick={(e) => { if (fromDate) setLeftView('time'); }}
                  >
                    <Box className="date-picker-card-main">
                      <AccessTimeOutlined className="date-picker-card-icon" />
                      <Typography className="date-picker-card-label">{t('timePicker.startTimeLabel', 'Start time')}</Typography>
                      <Typography className={`date-picker-card-value${!startTime ? ' date-picker-card-value--empty' : ''}`}>
                        {startTime ? format12h(startTime) : t('timePicker.startTime')}
                      </Typography>
                    </Box>
                    {startTime && (
                      <IconButton
                        size="small"
                        color="primary"
                        className="date-picker-clear-button"
                        onClick={(e) => { e.stopPropagation(); setStartTime(''); }}
                      >
                        <Clear sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                </>
              )}
            </Box>
            )}

            {/* End Card */}
            {!hideDueDate && (
              <Box className="date-picker-card">
              <Box 
                className={`date-picker-card-date is-clickable${rightView === 'date' ? ' is-active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setRightView('date'); }}
              >
                <Box className="date-picker-card-main">
                  <CalendarMonth className="date-picker-card-icon" />
                  <Typography className="date-picker-card-label">{DATE_PICKER_RANGE_LABELS.TO}</Typography>
                  <Typography className={`date-picker-card-value${!toDate ? ' date-picker-card-value--empty' : ''}`}>
                    {toDate ? new Date(toDate).toLocaleDateString('en-US') : DATE_PICKER_RANGE_LABELS.TO_PLACEHOLDER || t('calendar.selectDate', 'Select date')}
                  </Typography>
                </Box>
                {onClearDueDate && toDate && (
                  <IconButton
                    size="small"
                    color="primary"
                    className="date-picker-clear-button"
                    onClick={(e) => { e.stopPropagation(); handleClearDueDate(e); }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
              {showTime && (
                <>
                  <Box className="date-picker-card-divider" />
                  <Box 
                    className={`date-picker-card-time${toDate ? ' is-clickable' : ' date-picker-card-time--disabled'}${rightView === 'time' && toDate ? ' is-active' : ''}`}
                    onClick={(e) => { if (toDate) setRightView('time'); }}
                  >
                    <Box className="date-picker-card-main">
                      <AccessTimeOutlined className="date-picker-card-icon" />
                      <Typography className="date-picker-card-label">{t('timePicker.dueTimeLabel', 'Due time')}</Typography>
                      <Typography className={`date-picker-card-value${!dueTime ? ' date-picker-card-value--empty' : ''}`}>
                        {dueTime ? format12h(dueTime) : t('timePicker.dueTime')}
                      </Typography>
                    </Box>
                    {dueTime && (
                      <IconButton
                        size="small"
                        color="primary"
                        className="date-picker-clear-button"
                        onClick={(e) => { e.stopPropagation(); setDueTime(''); }}
                      >
                        <Clear sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                </>
              )}
            </Box>
            )}
          </Box>
          )}

          <Box className={`date-picker-range ${hideStartDate || hideDueDate ? 'date-picker-range--single' : 'date-picker-range--double'}`}>
            {!hideStartDate && (
              <Box className="date-picker-range-column">
              {leftView === 'time' ? (
                <TimePickerInline
                  value={startTime}
                  onChange={(val) => { setStartTime(val); setError(''); }}
                  headerLabel={t('timePicker.selectStartTime')}
                />
              ) : (
                <>
                  {selectedQuick !== 'custom' && (
                <Box className="date-picker-range-list">
                  {getQuickOptions().map((option) => (
                    <Box
                      key={option.id}
                      className={`date-picker-range-item${selectedQuick === option.id ? ' is-selected' : ''}`}
                      onClick={() => handleQuickSelect(option)}
                    >
                      <Typography className="date-picker-range-item-label">{option.label}</Typography>
                      {option.right && (
                        <Typography className="date-picker-range-item-right">{option.right}</Typography>
                      )}
                      {option.id === 'custom' && (
                        <CalendarMonth className="date-picker-range-item-custom-icon" fontSize="small" />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {selectedQuick === 'custom' && (
                <Box className="date-picker-inline-calendar">
                <Box className="date-picker-inline-calendar-header">
                  <IconButton size="small" onClick={() => {
                    const next = new Date(fromCalendarMonth);
                    next.setFullYear(next.getFullYear() - 1);
                    setFromCalendarMonth(next);
                  }}>
                    <KeyboardDoubleArrowLeft fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => {
                    const next = new Date(fromCalendarMonth);
                    next.setMonth(next.getMonth() - 1);
                    setFromCalendarMonth(next);
                  }}>
                    <ChevronLeft fontSize="small" />
                  </IconButton>
                    <Typography className="date-picker-inline-calendar-title">
                      {MONTHS[fromCalendarMonth.getMonth()]} {fromCalendarMonth.getFullYear()}
                    </Typography>
                  <IconButton size="small" onClick={() => {
                    const next = new Date(fromCalendarMonth);
                    next.setMonth(next.getMonth() + 1);
                    setFromCalendarMonth(next);
                  }}>
                    <ChevronRight fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => {
                    const next = new Date(fromCalendarMonth);
                    next.setFullYear(next.getFullYear() + 1);
                    setFromCalendarMonth(next);
                  }}>
                    <KeyboardDoubleArrowRight fontSize="small" />
                  </IconButton>
                </Box>
                  <Box className="date-picker-inline-calendar-weekdays">
                    {DATE_PICKER_WEEKDAYS.map((day) => (
                      <Box key={day} className="date-picker-inline-calendar-weekday">{day}</Box>
                    ))}
                  </Box>
                  <Box className="date-picker-inline-calendar-grid">
                    {getCalendarDays(fromCalendarMonth).map((day) => {
                      const isDisabled = (maxDate && day.dateString > maxDate) || (minDate && day.dateString < minDate) || (disabledDates && disabledDates.includes(day.dateString));
                      return (
                      <Box
                        key={day.dateString}
                        className={`date-picker-inline-calendar-day${day.isCurrentMonth ? '' : ' is-outside'}${fromDate === day.dateString ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}
                        onClick={() => {
                          if (isDisabled) return;
                          setFromDate(day.dateString);
                          setError('');
                          setFromCalendarMonth(new Date(day.dateObj.getFullYear(), day.dateObj.getMonth(), 1));
                          onSelectionChange?.({ fromDate: day.dateString, toDate, dates: [day.dateString] });
                          if (allowSingleSelection && autoApplySingleSelection) {
                            onDateChange?.([day.dateString]);
                            onClose();
                          }
                        }}
                      >
                        {day.date}
                      </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
                </>
              )}
            </Box>
            )}
            
            {!hideStartDate && !hideDueDate && (
              <Box className="date-picker-range-divider" />
            )}

            {!hideDueDate && (
              <Box className="date-picker-range-column">
              {rightView === 'time' ? (
                <TimePickerInline
                  value={dueTime}
                  onChange={(val) => { setDueTime(val); setError(''); }}
                  headerLabel={t('timePicker.selectDueTime')}
                />
              ) : (
              <Box className="date-picker-inline-calendar">
                <Box className="date-picker-inline-calendar-header">
                  <IconButton size="small" onClick={() => {
                    const next = new Date(toCalendarMonth);
                    next.setFullYear(next.getFullYear() - 1);
                    setToCalendarMonth(next);
                  }}>
                    <KeyboardDoubleArrowLeft fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => {
                    const next = new Date(toCalendarMonth);
                    next.setMonth(next.getMonth() - 1);
                    setToCalendarMonth(next);
                  }}>
                    <ChevronLeft fontSize="small" />
                  </IconButton>
                  <Typography className="date-picker-inline-calendar-title">
                    {MONTHS[toCalendarMonth.getMonth()]} {toCalendarMonth.getFullYear()}
                  </Typography>
                  <IconButton size="small" onClick={() => {
                    const next = new Date(toCalendarMonth);
                    next.setMonth(next.getMonth() + 1);
                    setToCalendarMonth(next);
                  }}>
                    <ChevronRight fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => {
                    const next = new Date(toCalendarMonth);
                    next.setFullYear(next.getFullYear() + 1);
                    setToCalendarMonth(next);
                  }}>
                    <KeyboardDoubleArrowRight fontSize="small" />
                  </IconButton>
                </Box>
                <Box className="date-picker-inline-calendar-weekdays">
                  {DATE_PICKER_WEEKDAYS.map((day) => (
                    <Box key={day} className="date-picker-inline-calendar-weekday">{day}</Box>
                  ))}
                </Box>
                <Box className="date-picker-inline-calendar-grid">
                  {getCalendarDays(toCalendarMonth).map((day) => {
                      const isDisabled = (maxDate && day.dateString > maxDate) || (minDate && day.dateString < minDate) || (disabledDates && disabledDates.includes(day.dateString));
                      return (
                      <Box
                        key={day.dateString}
                        className={`date-picker-inline-calendar-day${day.isCurrentMonth ? '' : ' is-outside'}${toDate === day.dateString ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}
                        onClick={() => {
                          if (isDisabled) return;
                          setToDate(day.dateString);
                          setError('');
                          setToCalendarMonth(new Date(day.dateObj.getFullYear(), day.dateObj.getMonth(), 1));
                          onSelectionChange?.({ fromDate, toDate: day.dateString, dates: fromDate ? [fromDate, day.dateString] : [day.dateString] });
                          if (allowSingleSelection && autoApplySingleSelection) {
                            onDateChange?.([day.dateString]);
                            onClose();
                          }
                        }}
                      >
                        {day.date}
                      </Box>
                      );
                  })}
                </Box>
              </Box>
              )}
            </Box>
            )}
          </Box>

          {helperText && (
            <Typography
              variant="caption"
              color="textSecondary"
              className="date-picker-helper"
            >
              {helperText}
            </Typography>
          )}
          {!hideActions && (
            <Box className={`date-picker-actions${helperText ? ' has-helper' : ''}${!hideStartDate && selectedQuick === 'custom' && leftView === 'date' ? ' has-back' : ''}`}>
              {!hideStartDate && selectedQuick === 'custom' && leftView === 'date' && (
                <Box className="date-picker-back-to-list" onClick={() => setSelectedQuick('')}>
                  <ChevronLeft fontSize="small" />
                  <span>{DATE_PICKER_BACK_TO_LIST}</span>
                </Box>
              )}
              <Box className="date-picker-actions-right">
                <Button onClick={onClose}>{t('calendar.cancel')}</Button>
                <Button variant="contained" onClick={handleDateRangeApply}>
                  {t('calendar.apply')}
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
      
      {mode === MODE_MONTH && (
        <>
          <Box className="date-picker-year-nav">
            <IconButton onClick={() => handleYearChange(-1)}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="h6">{selectedYear}</Typography>
            <IconButton onClick={() => handleYearChange(1)}>
              <ChevronRight />
            </IconButton>
          </Box>
          <Box className="date-picker-month-grid">
            {MONTHS.map((month, index) => (
              <Box
                key={month}
                className={`date-picker-month-card${selectedMonth === index ? " is-selected" : ""}`}
                onClick={() => handleMonthSelect(index)}
              >
                <Typography className="date-picker-month-card-title">{month}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      
      {mode === MODE_QUARTER && (
        <>
          <Box className="date-picker-year-nav">
            <IconButton onClick={() => handleYearChange(-1)}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="h6">{selectedYear}</Typography>
            <IconButton onClick={() => handleYearChange(1)}>
              <ChevronRight />
            </IconButton>
          </Box>
          <Box className="date-picker-quarters">
            {DATE_PICKER_QUARTERS.map((quarter, index) => (
              <Box
                key={quarter.label}
                className={`date-picker-quarter-card${selectedQuarter === index ? " is-selected" : ""}`}
                onClick={() => handleQuarterSelect(index)}
              >
                <Typography className="date-picker-quarter-title">{quarter.label}</Typography>
                <Box className="date-picker-quarter-divider" />
                <Box className="date-picker-quarter-months">
                  {quarter.months.map((month) => (
                    <Box key={month} className="date-picker-quarter-month">{month}</Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
      
      {mode === MODE_YEAR && (
        <Box className="date-picker-years" ref={yearScrollCallbackRef}>
          {generateYears().map((year) => (
            <Chip
              key={year}
              data-year={year}
              label={year}
              onClick={() => handleYearSelect(year)}
              variant={selectedYear === year ? "filled" : "outlined"}
              className="date-picker-year-chip"
            />
          ))}
        </Box>
      )}
    </Box>
  );

  if (anchorEl) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={4}
        PaperProps={{ 
          className: 'date-picker-popover'
        }}
      >
        {content}
      </Popover>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogContent>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default DatePickerModal;
